import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartColor } from '@t-one-internal/design-system';
import compApiClient from '../../api/compApiClient';

// ── Types ──

interface MyApproval {
  id: string;
  title: string;
  code: string;
  department: string;
  sender: string;
  senderRole: string;
  receivedDate: string;
  dueDate: string;
  urgency: string;
  stage: string;
  status: string;
  workflow_id: string;
  job_catalog_id: string;
  approval_status_id: string;
}

interface MyCollaboration {
  collaboration_history_id: string;
  job_catalog_id: string;
  status: string;
  deadline_date?: string;
  requested_date: string;
  job_title?: string;
  job_family_name?: string;
  role?: string;
}

interface DashboardSummary {
  jdSummary?: Array<{ totalJds: number; activeJds: number; inactiveJds: number; inprogressJds: number }>;
  jobFamilySummary?: Array<{ job_family: string; job_family_counts: number }>;
  collaboratorUserCounts?: { external_user: number; internal_user: number; total_users: number };
  collaboratorTitlesStatus?: { completed: number; pending: number; expired: number; total_counts: number };
  latestJds?: Array<{ id: string; job_title: string; job_family: string; owner: string; created_date: string; status: string }>;
  workflowStatusCounts?: { approved_count: number; pending_count: number; total_sum: number };
  workflowActiveInactiveCounts?: { active_count: number; inactive_count: number; total_count: number };
  jobArchitecture?: { jobGroups: number; jobFamilies: number; subFamilies: number; jobCodes: number; lineages: number };
}

interface TopApprover {
  approver_id: string;
  approver_name: string;
  approver_position: string;
  usage_count: number;
}

// ── Shared styles ──

const cardStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  fontFamily: 'system-ui, -apple-system, sans-serif',
};

const headerStyle: React.CSSProperties = {
  padding: '12px 16px',
  borderBottom: '1px solid #E5E7EB',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: 0,
};

const titleStyle: React.CSSProperties = {
  fontSize: '14px',
  fontWeight: 600,
  color: '#1F2937',
};

const badgeStyle = (color: string): React.CSSProperties => ({
  fontSize: '11px',
  fontWeight: 600,
  padding: '2px 8px',
  borderRadius: '9999px',
  backgroundColor: color,
  color: '#fff',
});

const emptyStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#9CA3AF',
  fontSize: '13px',
};

const spinnerStyle: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const Spinner: React.FC = () => (
  <div style={spinnerStyle}>
    <div
      style={{
        width: '24px',
        height: '24px',
        border: '3px solid #E5E7EB',
        borderTopColor: '#4B45E5',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
      }}
    />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ── Helpers ──

const urgencyColor = (u: string) => {
  switch (u?.toLowerCase()) {
    case 'high': return '#EF4444';
    case 'medium': return '#F59E0B';
    default: return '#6B7280';
  }
};

const statusColor = (s: string) => {
  switch (s?.toLowerCase()) {
    case 'pending': return '#F59E0B';
    case 'completed': case 'approved': case 'active': return '#10B981';
    case 'expired': case 'rejected': case 'inactive': return '#EF4444';
    case 'draft': case 'in-progress': return '#3B82F6';
    default: return '#6B7280';
  }
};

const formatDate = (d?: string) => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '—';
  }
};

const CHART_COLORS = Object.values(ChartColor);

// ═══════════════════════════════════════════
// 1. My Pending Approvals
// ═══════════════════════════════════════════

export const MyApprovalsWidget: React.FC<{
  data: MyApproval[];
  loading: boolean;
  error?: string;
}> = ({ data, loading, error }) => {
  const navigate = useNavigate();
  const pendingItems = data.filter(a => a.status?.toLowerCase() === 'pending');

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>My Pending Approvals</span>
        {pendingItems.length > 0 && (
          <span style={badgeStyle('#4B45E5')}>{pendingItems.length}</span>
        )}
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : pendingItems.length === 0 ? (
        <div style={emptyStyle}>No pending approvals</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {pendingItems.slice(0, 5).map((item) => (
            <div
              key={item.id || item.approval_status_id}
              onClick={() => navigate(`/comp-builder/job-builder/edit/${item.job_catalog_id}`)}
              style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', transition: 'background 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.title}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{item.sender} · {item.stage}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                  <span style={{ fontSize: '11px', color: urgencyColor(item.urgency), fontWeight: 600 }}>{item.urgency}</span>
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{formatDate(item.receivedDate)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 2. My Collaborations
// ═══════════════════════════════════════════

export const MyCollaborationsWidget: React.FC<{
  data: MyCollaboration[];
  loading: boolean;
  error?: string;
}> = ({ data, loading, error }) => {
  const navigate = useNavigate();
  const pendingItems = data.filter(c => c.status?.toLowerCase() === 'pending');

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>My Collaborations</span>
        {pendingItems.length > 0 && (
          <span style={badgeStyle('#8B5CF6')}>{pendingItems.length}</span>
        )}
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : pendingItems.length === 0 ? (
        <div style={emptyStyle}>No pending collaborations</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 0' }}>
          {pendingItems.slice(0, 5).map((item) => (
            <div
              key={item.collaboration_history_id}
              onClick={() => navigate(`/comp-builder/job-builder/edit/${item.job_catalog_id}`)}
              style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', transition: 'background 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.job_title || 'Untitled'}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{item.job_family_name || 'No family'} · {item.role || 'Collaborator'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                  <span style={{ ...badgeStyle(statusColor(item.status)), fontSize: '10px' }}>{item.status}</span>
                  {item.deadline_date && <span style={{ fontSize: '11px', color: '#9CA3AF' }}>Due {formatDate(item.deadline_date)}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 3. JD Quick Stats (compact KPI cards)
// ═══════════════════════════════════════════

export const JDQuickStatsWidget: React.FC<{
  summary: DashboardSummary | null;
  loading: boolean;
  error?: string;
}> = ({ summary, loading, error }) => {
  const jd = summary?.jdSummary?.[0];
  const wf = summary?.workflowStatusCounts;

  const stats = [
    { label: 'Total Titles', value: jd?.totalJds ?? 0, color: '#6366f1', bg: '#EEF2FF' },
    { label: 'Active', value: jd?.activeJds ?? 0, color: '#10B981', bg: '#ECFDF5' },
    { label: 'In Progress', value: jd?.inprogressJds ?? 0, color: '#3B82F6', bg: '#EFF6FF' },
    { label: 'Inactive', value: jd?.inactiveJds ?? 0, color: '#EF4444', bg: '#FEF2F2' },
    { label: 'Approved', value: wf?.approved_count ?? 0, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Pending', value: wf?.pending_count ?? 0, color: '#F59E0B', bg: '#FFFBEB' },
  ];

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Job Description Overview</span>
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : (
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', padding: '16px', alignContent: 'center' }}>
          {stats.map((s) => (
            <div key={s.label} style={{ backgroundColor: s.bg, borderRadius: '10px', padding: '12px', textAlign: 'center' }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 4. Latest Titles (recent JDs table)
// ═══════════════════════════════════════════

export const LatestTitlesWidget: React.FC<{
  titles: DashboardSummary['latestJds'];
  loading: boolean;
  error?: string;
}> = ({ titles, loading, error }) => {
  const navigate = useNavigate();
  const items = titles || [];

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Recent Titles</span>
        {items.length > 0 && <span style={badgeStyle('#6366f1')}>{items.length}</span>}
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : items.length === 0 ? (
        <div style={emptyStyle}>No titles yet</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: '4px 0' }}>
          {items.slice(0, 5).map((jd) => (
            <div
              key={jd.id}
              onClick={() => navigate(`/comp-builder/job-builder/edit/${jd.id}`)}
              style={{ padding: '10px 16px', cursor: 'pointer', borderBottom: '1px solid #F3F4F6', transition: 'background 0.15s' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#F9FAFB'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{jd.job_title}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{jd.job_family || '—'} · {jd.owner || '—'}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                  <span style={{ ...badgeStyle(statusColor(jd.status)), fontSize: '10px' }}>{jd.status}</span>
                  <span style={{ fontSize: '11px', color: '#9CA3AF' }}>{formatDate(jd.created_date)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 5. Job Family Distribution (horizontal bars)
// ═══════════════════════════════════════════

export const JobFamilyDistWidget: React.FC<{
  families: DashboardSummary['jobFamilySummary'];
  loading: boolean;
  error?: string;
}> = ({ families, loading, error }) => {
  const items = (families || []).filter(f => f.job_family_counts > 0).sort((a, b) => b.job_family_counts - a.job_family_counts);
  const maxVal = items.length > 0 ? items[0].job_family_counts : 1;

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Titles by Job Family</span>
        {items.length > 0 && <span style={badgeStyle('#ec4899')}>{items.length}</span>}
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : items.length === 0 ? (
        <div style={emptyStyle}>No data</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {items.slice(0, 6).map((f, idx) => {
            const pct = Math.max(10, (f.job_family_counts / maxVal) * 100);
            return (
              <div key={f.job_family} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span style={{ fontSize: '12px', color: '#4B5563', width: '110px', textAlign: 'right', flexShrink: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {f.job_family}
                </span>
                <div style={{ flex: 1, position: 'relative', height: '24px' }}>
                  <div style={{
                    height: '100%', borderRadius: '6px', backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                    width: `${pct}%`, minWidth: '40px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '8px',
                    transition: 'width 0.4s ease',
                  }}>
                    <span style={{ fontSize: '11px', fontWeight: 700, color: '#fff' }}>{f.job_family_counts}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 6. Workflow Status (donut-style visual)
// ═══════════════════════════════════════════

export const WorkflowStatusWidget: React.FC<{
  summary: DashboardSummary | null;
  loading: boolean;
  error?: string;
}> = ({ summary, loading, error }) => {
  const wf = summary?.workflowActiveInactiveCounts;
  const approvals = summary?.workflowStatusCounts;
  const active = wf?.active_count ?? 0;
  const inactive = wf?.inactive_count ?? 0;
  const total = wf?.total_count ?? 0;
  const approved = approvals?.approved_count ?? 0;
  const pending = approvals?.pending_count ?? 0;

  const items = [
    { label: 'Active Workflows', value: active, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Inactive Workflows', value: inactive, color: '#EF4444', bg: '#FEF2F2' },
    { label: 'Total Workflows', value: total, color: '#6366f1', bg: '#EEF2FF' },
    { label: 'Approved', value: approved, color: '#10B981', bg: '#ECFDF5' },
    { label: 'Pending Approval', value: pending, color: '#F59E0B', bg: '#FFFBEB' },
  ];

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Workflow Overview</span>
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : (
        <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '10px', justifyContent: 'center' }}>
          {items.map((s) => (
            <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: s.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: s.color }}>{s.value}</span>
              </div>
              <span style={{ fontSize: '13px', color: '#4B5563' }}>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 7. Collaboration Overview
// ═══════════════════════════════════════════

export const CollaborationOverviewWidget: React.FC<{
  summary: DashboardSummary | null;
  loading: boolean;
  error?: string;
}> = ({ summary, loading, error }) => {
  const users = summary?.collaboratorUserCounts;
  const status = summary?.collaboratorTitlesStatus;

  const stats = [
    { label: 'Internal', value: users?.internal_user ?? 0, color: '#6366f1', bg: '#EEF2FF' },
    { label: 'External', value: users?.external_user ?? 0, color: '#ec4899', bg: '#FDF2F8' },
    { label: 'Total Collaborators', value: users?.total_users ?? 0, color: '#3B82F6', bg: '#EFF6FF' },
  ];

  const statusStats = [
    { label: 'Completed', value: status?.completed ?? 0, color: '#10B981' },
    { label: 'Pending', value: status?.pending ?? 0, color: '#F59E0B' },
    { label: 'Expired', value: status?.expired ?? 0, color: '#EF4444' },
  ];
  const totalStatus = status?.total_counts || 1;

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Collaboration Overview</span>
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : (
        <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', gap: '16px', justifyContent: 'center' }}>
          {/* Collaborator counts */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {stats.map((s) => (
              <div key={s.label} style={{ flex: 1, backgroundColor: s.bg, borderRadius: '10px', padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: '20px', fontWeight: 700, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: '10px', color: '#6B7280', marginTop: '2px' }}>{s.label}</div>
              </div>
            ))}
          </div>
          {/* Response progress bars */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {statusStats.map((s) => {
              const pct = totalStatus > 0 ? Math.round((s.value / totalStatus) * 100) : 0;
              return (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', color: '#4B5563', width: '75px', flexShrink: 0 }}>{s.label}</span>
                  <div style={{ flex: 1, height: '8px', backgroundColor: '#F3F4F6', borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${pct}%`, backgroundColor: s.color, borderRadius: '4px', transition: 'width 0.4s ease' }} />
                  </div>
                  <span style={{ fontSize: '11px', fontWeight: 600, color: s.color, width: '35px', textAlign: 'right' }}>{pct}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};


// ═══════════════════════════════════════════
// 8. Top Approvers (leaderboard)
// ═══════════════════════════════════════════

export const TopApproversWidget: React.FC<{
  data: TopApprover[];
  loading: boolean;
  error?: string;
}> = ({ data, loading, error }) => {
  const items = data.slice(0, 5);
  const maxCount = items.length > 0 ? items[0].usage_count : 1;

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Top Approvers</span>
        {items.length > 0 && <span style={badgeStyle('#6366f1')}>{items.length}</span>}
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : items.length === 0 ? (
        <div style={emptyStyle}>No approvers configured</div>
      ) : (
        <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {items.map((a, idx) => {
            const pct = Math.max(15, (a.usage_count / maxCount) * 100);
            return (
              <div key={a.approver_id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                  backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] + '18',
                  color: CHART_COLORS[idx % CHART_COLORS.length],
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '12px', fontWeight: 700,
                }}>
                  {idx + 1}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#1F2937', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.approver_name}
                  </div>
                  <div style={{ marginTop: '4px', position: 'relative', height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', overflow: 'hidden' }}>
                    <div style={{
                      height: '100%', borderRadius: '3px', width: `${pct}%`,
                      backgroundColor: CHART_COLORS[idx % CHART_COLORS.length],
                      transition: 'width 0.4s ease',
                    }} />
                  </div>
                </div>
                <span style={{ fontSize: '12px', fontWeight: 700, color: '#4B5563', flexShrink: 0 }}>{a.usage_count}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// 9. Job Architecture Summary
// ═══════════════════════════════════════════

export const JobArchitectureWidget: React.FC<{
  summary: DashboardSummary | null;
  loading: boolean;
  error?: string;
}> = ({ summary, loading, error }) => {
  const navigate = useNavigate();
  const arch = summary?.jobArchitecture;

  const items = [
    { label: 'Job Groups', value: arch?.jobGroups ?? 0, color: '#6366f1', bg: '#EEF2FF', path: '/comp-builder/job-builder/settings/job-groups' },
    { label: 'Job Families', value: arch?.jobFamilies ?? 0, color: '#8B5CF6', bg: '#F5F3FF', path: '/comp-builder/job-builder/settings/job-families' },
    { label: 'Sub-Families', value: arch?.subFamilies ?? 0, color: '#ec4899', bg: '#FDF2F8', path: '/comp-builder/job-builder/settings/sub-job-families' },
    { label: 'Job Codes', value: arch?.jobCodes ?? 0, color: '#3B82F6', bg: '#EFF6FF', path: '/comp-builder/job-builder/settings/job-codes' },
    { label: 'Lineages', value: arch?.lineages ?? 0, color: '#10B981', bg: '#ECFDF5', path: '/comp-builder/job-builder/job-architecture' },
  ];

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <span style={titleStyle}>Job Architecture</span>
      </div>
      {loading ? <Spinner /> : error ? (
        <div style={emptyStyle}>{error}</div>
      ) : (
        <div style={{ flex: 1, padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '8px', justifyContent: 'center' }}>
          {items.map((item) => (
            <div
              key={item.label}
              onClick={() => navigate(item.path)}
              style={{
                display: 'flex', alignItems: 'center', gap: '12px', padding: '8px 10px',
                borderRadius: '10px', cursor: 'pointer', transition: 'background 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = item.bg; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <div style={{
                width: '40px', height: '40px', borderRadius: '10px', backgroundColor: item.bg,
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <span style={{ fontSize: '16px', fontWeight: 700, color: item.color }}>{item.value}</span>
              </div>
              <span style={{ fontSize: '13px', fontWeight: 500, color: '#4B5563' }}>{item.label}</span>
              <span style={{ marginLeft: 'auto', fontSize: '16px', color: '#D1D5DB' }}>&rsaquo;</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════
// Hook: useCompBldrMyData
// ═══════════════════════════════════════════

export const useCompBldrMyData = (enabled: boolean) => {
  const [approvals, setApprovals] = useState<MyApproval[]>([]);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvalsError, setApprovalsError] = useState<string | null>(null);

  const [collaborations, setCollaborations] = useState<MyCollaboration[]>([]);
  const [collaborationsLoading, setCollaborationsLoading] = useState(false);
  const [collaborationsError, setCollaborationsError] = useState<string | null>(null);

  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);

  const [topApprovers, setTopApprovers] = useState<TopApprover[]>([]);
  const [topApproversLoading, setTopApproversLoading] = useState(false);
  const [topApproversError, setTopApproversError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setApprovals([]);
      setCollaborations([]);
      setSummary(null);
      setTopApprovers([]);
      return;
    }

    // Fetch my pending approvals
    setApprovalsLoading(true);
    compApiClient
      .get('/job-builder/workflows/my-approvals', { params: { status: 'pending', limit: 5 } })
      .then((res) => {
        const items = res.data?.data || res.data || [];
        setApprovals(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error('[useCompBldrMyData] approvals error:', err);
        setApprovalsError('Failed to load approvals');
      })
      .finally(() => setApprovalsLoading(false));

    // Fetch my pending collaborations
    setCollaborationsLoading(true);
    compApiClient
      .get('/job-builder/my-collaborations', { params: { status: 'pending', limit: 5 } })
      .then((res) => {
        const items = res.data?.data || res.data || [];
        setCollaborations(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error('[useCompBldrMyData] collaborations error:', err);
        setCollaborationsError('Failed to load collaborations');
      })
      .finally(() => setCollaborationsLoading(false));

    // Fetch dashboard summary (org-level stats)
    setSummaryLoading(true);
    compApiClient
      .get('/job-builder/dashboard/summary')
      .then((res) => {
        const data = res.data?.dashboard || res.data?.data || res.data || {};
        setSummary(data);
      })
      .catch((err) => {
        console.error('[useCompBldrMyData] summary error:', err);
        setSummaryError('Failed to load summary');
      })
      .finally(() => setSummaryLoading(false));

    // Fetch top approvers
    setTopApproversLoading(true);
    compApiClient
      .get('/job-builder/dashboard/top-approvers')
      .then((res) => {
        const items = res.data?.data || res.data || [];
        setTopApprovers(Array.isArray(items) ? items : []);
      })
      .catch((err) => {
        console.error('[useCompBldrMyData] top approvers error:', err);
        setTopApproversError('Failed to load approvers');
      })
      .finally(() => setTopApproversLoading(false));
  }, [enabled]);

  return {
    approvals, approvalsLoading, approvalsError,
    collaborations, collaborationsLoading, collaborationsError,
    summary, summaryLoading, summaryError,
    topApprovers, topApproversLoading, topApproversError,
  };
};
